import React, { useState, useEffect } from 'react';
import { createUser, getOrganizations, getOrganizationsByUser, getTeamsByOrganization, getTeamsByAgentur, getAgenturById, getAgenturenByOrganization } from '../api';
import { useNavigate, useLocation } from 'react-router-dom';

function CreateUser({ user, onClose, preselectedTeamId, preselectedAgenturId, hideOrganizationFields = false, showAgenturSelection = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const urlTeamId = queryParams.get('teamId');
  const finalTeamId = preselectedTeamId || urlTeamId;
  
  // Grundlegende Benutzerdaten
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    fullName: '',
    roleType: '0', // 0 = Normal, 1 = Admin, 2 = Teamleiter, 3 = Organisationsleiter (neu)
    organizationId: '',
    teamId: finalTeamId || '',
    agenturId: preselectedAgenturId || '' // NEU: Agentur-ID vorbelegen
  });
  
  // Organisationen und Teams für die Auswahl
  const [organizations, setOrganizations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [organizationsLoading, setOrganizationsLoading] = useState(false);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [agenturInfo, setAgenturInfo] = useState(null);
  const [agenturTeams, setAgenturTeams] = useState([]); // NEU: Agentur-Teams
  
  // NEU: Agenturen für die Auswahl (für Organization-Members-Seite)
  const [availableAgenturen, setAvailableAgenturen] = useState([]);
  const [agenturenLoading, setAgenturenLoading] = useState(false);
  
  // Status für UI
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  
  // Benutzerrechte bestimmen
  const isAdmin = user && user.isAdmin;
  const isOrgLeader = user && user.isOrgLeader;
  const isTeamLeader = user && user.isTeamlead;
  const userOrgId = (isOrgLeader || isTeamLeader) ? user.organization_id : null;
  const userTeamId = isTeamLeader ? user.team?.id : null;

  // Prüfen ob als Dialog verwendet wird
  const isDialog = !!onClose;

  // Agentur-Informationen laden
  const loadAgenturInfo = async () => {
    if (!preselectedAgenturId) return;
    
    try {
      const response = await getAgenturById(user.id, preselectedAgenturId);
      if (response.success) {
        setAgenturInfo(response.agentur);
        // Automatisch die Organisation setzen
        setFormData(prev => ({ 
          ...prev, 
          organizationId: response.agentur.organization_id.toString(),
          agenturId: preselectedAgenturId 
        }));
      } else {
        setError('Fehler beim Laden der Agentur-Informationen: ' + response.error);
      }
    } catch (err) {
      setError('Netzwerkfehler beim Laden der Agentur-Informationen: ' + err.message);
    }
  };

  // Organisationen laden
  const loadOrganizations = async () => {
    setOrganizationsLoading(true);
    setError('');
    try {
      let response;
      if (isAdmin) {
        response = await getOrganizations(user.id);
      } else {
        response = await getOrganizationsByUser(user.id);
      }
      if (response.success) {
        setOrganizations(response.organizations || []);
        if (response.organizations && response.organizations.length === 1) {
          setFormData(prev => ({ ...prev, organizationId: response.organizations[0].id }));
        }
      } else {
        setError('Fehler beim Laden der Organisationen: ' + response.error);
      }
    } catch (err) {
      setError('Netzwerkfehler beim Laden der Organisationen: ' + err.message);
    } finally {
      setOrganizationsLoading(false);
    }
  };

  // Teams laden - entweder Organisations-Teams oder Agentur-Teams
  const loadTeams = async (organizationId) => {
    if (!organizationId && !formData.agenturId && !preselectedAgenturId && !agenturInfo) {
      setTeams([]);
      return;
    }
    setTeamsLoading(true);
    try {
      let response;
      
      if (formData.agenturId || preselectedAgenturId) {
        // Wenn Agentur-ID vorhanden ist, lade Teams aus der teams Tabelle für die Organisation
        if (agenturInfo && agenturInfo.organization_id) {
          response = await getTeamsByOrganization(user.id, agenturInfo.organization_id);
        } else {
          // Fallback: Wenn keine Agentur-Info verfügbar ist, versuche es mit der Organisation des Users
          response = await getTeamsByOrganization(user.id, user.organization_id);
        }
      } else {
        // Ansonsten lade Organisations-Teams
        response = await getTeamsByOrganization(user.id, organizationId);
      }
      
      if (response.success) {
        setTeams(response.teams);
      } else {
        setError('Fehler beim Laden der Teams: ' + response.error);
      }
    } catch (err) {
      setError('Netzwerkfehler beim Laden der Teams: ' + err.message);
    } finally {
      setTeamsLoading(false);
    }
  };

  // Agentur-Teams laden
  const loadAgenturTeams = async (agenturId) => {
    if (!agenturId && !preselectedAgenturId) {
      setAgenturTeams([]);
      return;
    }
    const currentAgenturId = agenturId || preselectedAgenturId;
    try {
      const response = await getTeamsByAgentur(user.id, currentAgenturId);
      if (response.success) {
        setAgenturTeams(response.teams);
      } else {
        setAgenturTeams([]);
      }
    } catch (err) {
      setAgenturTeams([]);
    }
  };

  // NEU: Agenturen für die Auswahl laden (für Organization-Members-Seite)
  const loadAvailableAgenturen = async (organizationId = null) => {
    // Agenturen laden wenn eine organization_id vorhanden ist (egal ob aus Formular, User oder Parameter)
    const organizationIdToUse = organizationId || formData.organizationId || user?.organization_id;
    if (!organizationIdToUse) {
      setAvailableAgenturen([]);
      return;
    }
    
    setAgenturenLoading(true);
    try {
      const response = await getAgenturenByOrganization(organizationIdToUse);
      if (response.success) {
        setAvailableAgenturen(response.agenturen || []);
      } else {
        setError('Fehler beim Laden der Agenturen: ' + response.error);
        setAvailableAgenturen([]);
      }
    } catch (err) {
      setError('Netzwerkfehler beim Laden der Agenturen: ' + err.message);
      setAvailableAgenturen([]);
    } finally {
      setAgenturenLoading(false);
    }
  };

  // Formular-Handler
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (name === 'organizationId') {
      setFormData(prev => ({ ...prev, teamId: '' }));
      loadTeams(value);
      // Agenturen für die neue Organisation laden
      if (value) {
        loadAvailableAgenturen(value);
      }
    }
    if (name === 'agenturId') {
      setFormData(prev => ({ ...prev, a_team_id: '' }));
      loadAgenturTeams(value);
    }
    if (error) {
      setError('');
    }
  };
  

  // Initial laden
  useEffect(() => {
    if (preselectedAgenturId) {
      loadAgenturInfo();
    } else {
      loadOrganizations();
    }
    
    // Agenturen laden, wenn showAgenturSelection aktiviert ist ODER wenn organization_id vorhanden ist
    if (showAgenturSelection && user?.organization_id) {
      loadAvailableAgenturen(user.organization_id);
    }
  }, [preselectedAgenturId, showAgenturSelection]);
  
  // Agenturen neu laden, wenn sich organizationId ändert
  useEffect(() => {
    if (formData.organizationId) {
      loadAvailableAgenturen(formData.organizationId);
    }
  }, [formData.organizationId]);

  // Teams laden wenn sich organizationId oder agenturId ändert
  useEffect(() => {
    if (formData.organizationId || formData.agenturId || preselectedAgenturId || agenturInfo) {
      loadTeams(formData.organizationId);
    }
    if (formData.agenturId || preselectedAgenturId) {
      loadAgenturTeams(formData.agenturId || preselectedAgenturId);
    } else {
      setAgenturTeams([]);
    }
  }, [formData.organizationId, formData.agenturId, preselectedAgenturId, agenturInfo]);



  // Wenn preselectedAgenturId sich ändert, setze agenturId im formData
  useEffect(() => {
    if (preselectedAgenturId) {
      setFormData(prev => ({ ...prev, agenturId: preselectedAgenturId }));
    }
  }, [preselectedAgenturId]);

  // Formular absenden
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (formData.password !== formData.confirmPassword) {
      setError('Passwörter stimmen nicht überein.');
      return;
    }
    if (formData.password.length < 6) {
      setError('Passwort muss mindestens 6 Zeichen lang sein.');
      return;
    }
    if (!formData.organizationId && !formData.agenturId) {
      setError('Bitte eine Organisation oder Agentur auswählen.');
      return;
    }
    // Agentur-Team ist optional - keine Validierung erforderlich
    let finalTeamId = formData.teamId;
    if (isTeamLeader && (!finalTeamId || finalTeamId === '')) {
      if (preselectedTeamId) {
        finalTeamId = preselectedTeamId;
      } else if (userTeamId) {
        finalTeamId = userTeamId;
      }
    }
    const userPayload = {
      ...formData,
      agenturId: formData.agenturId ? formData.agenturId : 0,
      organizationId: formData.organizationId,
      teamId: finalTeamId,
      a_team_id: formData.agenturId ? formData.a_team_id : 0, // NEU: 0 wenn keine Agentur
      fullName: formData.fullName,
      roleType: formData.roleType,
      email: formData.email,
      password: formData.password
    };
    setLoading(true);
    try {
      const response = await createUser(userPayload, user.id);
      if (response.success) {
        setSuccess('Benutzer erfolgreich erstellt!');
        setFormData({
          email: '',
          password: '',
          confirmPassword: '',
          fullName: '',
          roleType: '0',
          organizationId: '',
          teamId: '',
          agenturId: preselectedAgenturId || ''
        });
        if (onClose) onClose();
      } else {
        setError(response.error || 'Fehler beim Erstellen des Benutzers');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Wenn der Benutzer weder Admin noch Organisationsleiter noch Teamleiter ist, zur Hauptseite zurückkehren
  useEffect(() => {
    if (!isDialog && !isAdmin && !isOrgLeader && !isTeamLeader) {
      navigate('/');
    }
  }, [isAdmin, isOrgLeader, isTeamLeader, navigate, isDialog]);

  if (!isDialog && !isAdmin && !isOrgLeader && !isTeamLeader) {
    return <div className="loading">Prüfe Berechtigungen...</div>;
  }

  // Als Dialog verwenden
  if (isDialog) {
    return (
      <div className="create-user-dialog">
        <style>{`
          .create-user-dialog .form-row {
            display: flex;
            gap: 20px;
            margin-bottom: 20px;
          }
          
          .create-user-dialog .form-field {
            flex: 1;
            min-width: 0;
          }
          
          .create-user-dialog .form-field label {
            display: block;
            margin-bottom: 5px;
            font-weight: 500;
            color: #374151;
          }
          
          .create-user-dialog .form-field input,
          .create-user-dialog .form-field select {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            transition: border-color 0.2s;
          }
          
          .create-user-dialog .form-field input:focus,
          .create-user-dialog .form-field select:focus {
            outline: none;
            border-color: #3b82f6;
            box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
          }
          
          .create-user-dialog .form-section {
            margin-bottom: 30px;
          }
          
          .create-user-dialog .form-section h3 {
            margin: 0 0 15px 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: #111827;
            border-bottom: 2px solid #e5e7eb;
            padding-bottom: 8px;
          }
          
          .create-user-dialog .helper-text {
            font-size: 12px;
            color: #6b7280;
            margin-top: 4px;
          }
          
          .create-user-dialog .loading-indicator {
            font-size: 12px;
            color: #3b82f6;
            font-style: italic;
          }
          
          .create-user-dialog .role-info {
            margin-top: 10px;
            padding: 10px;
            background-color: #f8f9fa;
            border-radius: 4px;
            border-left: 3px solid #3b82f6;
          }
          
          .create-user-dialog .role-info ul {
            margin: 5px 0 0 20px;
            padding: 0;
          }
          
          .create-user-dialog .role-info li {
            margin-bottom: 3px;
          }
          
          .create-user-dialog .form-actions {
            display: flex;
            justify-content: flex-end;
            gap: 10px;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e5e7eb;
          }
          
          .create-user-dialog .cancel-button {
            padding: 10px 20px;
            border: 1px solid #d1d5db;
            background: white;
            color: #374151;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;
          }
          
          .create-user-dialog .cancel-button:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
          
          .create-user-dialog button[type="submit"] {
            padding: 10px 20px;
            background: #3b82f6;
            color: white;
            border: none;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: background-color 0.2s;
          }
          
          .create-user-dialog button[type="submit"]:hover:not(:disabled) {
            background: #2563eb;
          }
          
          .create-user-dialog button[type="submit"]:disabled {
            background: #9ca3af;
            cursor: not-allowed;
          }
          
          .create-user-dialog .error-message {
            background: #fee2e2;
            color: #dc2626;
            padding: 12px;
            border-radius: 6px;
            margin-bottom: 20px;
            border: 1px solid #fecaca;
          }
          
          .create-user-dialog .success-message {
            background: #d1fae5;
            color: #065f46;
            padding: 12px;
            border-radius: 6px;
            margin-bottom: 20px;
            border: 1px solid #a7f3d0;
          }
          
          @media (max-width: 768px) {
            .create-user-dialog .form-row {
              flex-direction: column;
              gap: 15px;
            }
          }
        `}</style>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}
        
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-section">
            <h3>Benutzerdaten</h3>
            
            <div className="form-row">
              <div className="form-field">
                <label>E-Mail*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Voller Name*</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Passwort*</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Passwort bestätigen*</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
          
                    {!hideOrganizationFields && (
            <div className="form-section">
              <h3>Organisation und Team</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Organisation*</label>
                  <select
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    required
                    disabled={organizationsLoading}
                  >
                    <option value="">-- Organisation auswählen --</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {organizationsLoading && <span className="loading-indicator">Lädt...</span>}
                </div>
                <div className="form-field">
                  <label>Projekt*</label>
                  {isTeamLeader ? (
                    // Teamleiter sieht nur sein eigenes Team (readonly)
                    <select
                      name="teamId"
                      value={formData.teamId || (user?.team?.id || '')}
                      onChange={handleChange}
                      required
                      disabled
                    >
                      {teams
                        .filter(team => team.id === (preselectedTeamId || userTeamId))
                        .map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name} {team.isDefault ? '(Standard)' : ''}
                          </option>
                        ))}
                      {(!teams.length || !teams.some(t => t.id === (preselectedTeamId || userTeamId))) && (
                        <option value={preselectedTeamId || userTeamId}>
                          Mein Projekt
                        </option>
                      )}
                    </select>
                  ) : (
                    // Admin und Organisationsleiter können Teams auswählen
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleChange}
                      required
                      disabled={!formData.organizationId || teamsLoading}
                    >
                      <option value="">-- Projekt auswählen --</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.isDefault ? '(Standard)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {teamsLoading && <span className="loading-indicator">Lädt...</span>}
                  {!formData.organizationId && (
                    <div className="helper-text">Bitte zuerst eine Organisation auswählen</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {!hideOrganizationFields && (
            <div className="form-section">
              <h3>Agentur</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Agentur*</label>
                  <select
                    name="agenturId"
                    value={formData.agenturId}
                    onChange={handleChange}
                    required
                    disabled={agenturenLoading}
                  >
                    <option value="">-- Agentur auswählen --</option>
                    {availableAgenturen.map(agentur => (
                      <option key={agentur.id} value={agentur.id}>
                        {agentur.name}
                      </option>
                    ))}
                  </select>
                  {agenturenLoading && <span className="loading-indicator">Lädt...</span>}
                </div>
                <div className="form-field">
                  <label>Agentur-Team (optional)</label>
                  <select
                    name="a_team_id"
                    value={formData.a_team_id || ''}
                    onChange={handleChange}
                    disabled={!formData.agenturId}
                  >
                    <option value="">-- Agentur-Team auswählen (optional) --</option>
                    {agenturTeams.map(team => (
                      <option key={team.id} value={team.id}>{team.name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {!hideOrganizationFields && (
            <div className="form-section">
              <h3>Rolle und Berechtigungen</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Rolle*</label>
                  <select
                    name="roleType"
                    value={formData.roleType}
                    onChange={handleChange}
                    required
                  >
                    <option value="0">Normaler Benutzer</option>
                    {isAdmin && <option value="1">Administrator</option>}
                    {(isAdmin || isOrgLeader) && <option value="2">Teamleiter</option>}
                    {isAdmin && <option value="3">Organisationsleiter</option>}
                    {isAdmin && <option value="4">Callcenter Leader</option>}
                    {isAdmin && <option value="5">Callcenter Mitarbeiter</option>}
                    {(isAdmin || isOrgLeader) && <option value="6">Fachberater</option>}
                    {(isAdmin || isOrgLeader) && <option value="7">Agentur Leiter</option>}
                  </select>
                  
                  <div className="helper-text">
                    {formData.roleType === '0' && 'Standard-Benutzer mit grundlegenden Berechtigungen'}
                    {formData.roleType === '1' && 'Vollzugriff auf alle Funktionen und Einstellungen'}
                    {formData.roleType === '2' && 'Verwaltung des eigenen Teams und Team-Mitglieder'}
                    {formData.roleType === '3' && 'Verwaltung der gesamten Organisation und aller Teams'}
                    {formData.roleType === '4' && 'Verwaltung des Callcenters und Callcenter-Mitarbeiter'}
                    {formData.roleType === '5' && 'Callcenter-spezifische Berechtigungen'}
                    {formData.roleType === '6' && 'Fachberater-spezifische Berechtigungen'}
                    {formData.roleType === '7' && 'Agentur-spezifische Berechtigungen'}
                  </div>
                  
                  {formData.roleType === '2' && (
                    <div className="role-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <strong>Teamleiter</strong> können:
                      <ul style={{ margin: '5px 0 0 20px' }}>
                        <li>Alle Teammitglieder verwalten</li>
                        <li>Team-Dashboards einsehen</li>
                      </ul>
                    </div>
                  )}
                  
                  {formData.roleType === '3' && (
                    <div className="role-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <strong>Organisationsleiter</strong> können:
                      <ul style={{ margin: '5px 0 0 20px' }}>
                        <li>Teams erstellen und verwalten</li>
                        <li>Benutzer hinzufügen und verwalten</li>
                        <li>Organisationsdaten bearbeiten</li>
                      </ul>
                    </div>
                  )}

                  {formData.roleType === '6' && (
                    <div className="role-info" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                      <strong>Fachberater</strong> können:
                      <ul style={{ margin: '5px 0 0 20px' }}>
                        <li>Ihre zugewiesenen Termine einsehen</li>
                        <li>Termin-Details und Kundeninformationen abrufen</li>
                        <li>Zur Kundendetailseite navigieren</li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* NEU: Team-Auswahl auch anzeigen, wenn Organisationsfelder ausgeblendet sind */}
          {hideOrganizationFields && (
            <div className="form-section">
              <h3>Projekt und Team</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Projekt*</label>
                  {isTeamLeader ? (
                    // Teamleiter sieht nur sein eigenes Team (readonly)
                    <select
                      name="teamId"
                      value={formData.teamId || (user?.team?.id || '')}
                      onChange={handleChange}
                      required
                      disabled
                    >
                      {teams
                        .filter(team => team.id === (preselectedTeamId || userTeamId))
                        .map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name} {team.isDefault ? '(Standard)' : ''}
                          </option>
                        ))}
                      {(!teams.length || !teams.some(t => t.id === (preselectedTeamId || userTeamId))) && (
                        <option value={preselectedTeamId || userTeamId}>
                          Mein Projekt
                        </option>
                      )}
                    </select>
                  ) : (
                    // Admin und Organisationsleiter können Teams auswählen
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleChange}
                      required
                      disabled={teamsLoading}
                    >
                      <option value="">-- Projekt auswählen --</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.isDefault ? '(Standard)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {teamsLoading && <span className="loading-indicator">Lädt...</span>}
                </div>
                {/* Agentur-Team Auswahl */}
                {(formData.agenturId || preselectedAgenturId) && (
                  <div className="form-field">
                    <label>Agentur-Team (optional)</label>
                    <select
                      name="a_team_id"
                      value={formData.a_team_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">-- Agentur-Team auswählen (optional) --</option>
                      {agenturTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
          
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={onClose}
            >
              Abbrechen
            </button>
            <button 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Wird erstellt...' : 'Benutzer erstellen'}
            </button>
          </div>
          
        </form>
      </div>
    );
  }

  // Als eigenständige Seite verwenden
  return (
    <div className="create-user-container">
      <div className="create-user-header">
        <h2>Neuen Benutzer erstellen</h2>
        <button className="back-button" onClick={() => navigate('/dashboard')}>
          Zurück zum Dashboard
        </button>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      <div className="create-user-form-container">
        <form onSubmit={handleSubmit} className="create-user-form">
          <div className="form-section">
            <h3>Benutzerdaten</h3>
            
            <div className="form-row">
              <div className="form-field">
                <label>E-Mail*</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Voller Name*</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-field">
                <label>Passwort*</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-field">
                <label>Passwort bestätigen*</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
          
          {!hideOrganizationFields && (
            <div className="form-section">
              <h3>Organisation und Team</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Organisation*</label>
                  <select
                    name="organizationId"
                    value={formData.organizationId}
                    onChange={handleChange}
                    required
                    disabled={organizationsLoading}
                  >
                    <option value="">-- Organisation auswählen --</option>
                    {organizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                  {organizationsLoading && <span className="loading-indicator">Lädt...</span>}
                </div>
                <div className="form-field">
                  <label>Projekt*</label>
                  {isTeamLeader ? (
                    // Teamleiter sieht nur sein eigenes Team (readonly)
                    <select
                      name="teamId"
                      value={formData.teamId || (user?.team?.id || '')}
                      onChange={handleChange}
                      required
                      disabled
                    >
                      {teams
                        .filter(team => team.id === (preselectedTeamId || userTeamId))
                        .map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name} {team.isDefault ? '(Standard)' : ''}
                          </option>
                        ))}
                      {(!teams.length || !teams.some(t => t.id === (preselectedTeamId || userTeamId))) && (
                        <option value={preselectedTeamId || userTeamId}>
                          Mein Projekt
                        </option>
                      )}
                    </select>
                  ) : (
                    // Admin und Organisationsleiter können Teams auswählen
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleChange}
                      required
                      disabled={!formData.organizationId || teamsLoading}
                    >
                      <option value="">-- Projekt auswählen --</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.isDefault ? '(Standard)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {teamsLoading && <span className="loading-indicator">Lädt...</span>}
                  {!formData.organizationId && (
                    <div className="helper-text">Bitte zuerst eine Organisation auswählen</div>
                  )}
                </div>
                {/* Agentur-Team Auswahl */}
                {formData.agenturId && (
                  <div className="form-field">
                    <label>Agentur-Team (optional)</label>
                    <select
                      name="a_team_id"
                      value={formData.a_team_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">-- Agentur-Team auswählen (optional) --</option>
                      {agenturTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* NEU: Team-Auswahl auch anzeigen, wenn Organisationsfelder ausgeblendet sind */}
          {hideOrganizationFields && (
            <div className="form-section">
              <h3>Projekt und Team</h3>
              
              <div className="form-row">
                <div className="form-field">
                  <label>Projekt*</label>
                  {isTeamLeader ? (
                    // Teamleiter sieht nur sein eigenes Team (readonly)
                    <select
                      name="teamId"
                      value={formData.teamId || (user?.team?.id || '')}
                      onChange={handleChange}
                      required
                      disabled
                    >
                      {teams
                        .filter(team => team.id === (preselectedTeamId || userTeamId))
                        .map(team => (
                          <option key={team.id} value={team.id}>
                            {team.name} {team.isDefault ? '(Standard)' : ''}
                          </option>
                        ))}
                      {(!teams.length || !teams.some(t => t.id === (preselectedTeamId || userTeamId))) && (
                        <option value={preselectedTeamId || userTeamId}>
                          Mein Projekt
                        </option>
                      )}
                    </select>
                  ) : (
                    // Admin und Organisationsleiter können Teams auswählen
                    <select
                      name="teamId"
                      value={formData.teamId}
                      onChange={handleChange}
                      required
                      disabled={teamsLoading}
                    >
                      <option value="">-- Projekt auswählen --</option>
                      {teams.map(team => (
                        <option key={team.id} value={team.id}>
                          {team.name} {team.isDefault ? '(Standard)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {teamsLoading && <span className="loading-indicator">Lädt...</span>}
                </div>
                {/* Agentur-Team Auswahl */}
                {(formData.agenturId || preselectedAgenturId) && (
                  <div className="form-field">
                    <label>Agentur-Team (optional)</label>
                    <select
                      name="a_team_id"
                      value={formData.a_team_id || ''}
                      onChange={handleChange}
                    >
                      <option value="">-- Agentur-Team auswählen (optional) --</option>
                      {agenturTeams.map(team => (
                        <option key={team.id} value={team.id}>{team.name}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>
          )}
          
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button" 
              onClick={() => navigate('/dashboard')}
            >
              Abbrechen
            </button>
            <button 
              type="submit" 
              disabled={loading}
            >
              {loading ? 'Wird erstellt...' : 'Benutzer erstellen'}
            </button>
          </div>
          
        </form>
      </div>
    </div>
  );
}

export default CreateUser;